import styled from '@emotion/styled'
import { ExtranetShopProductDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetTextDiv,
  MediaQuery,
  formatDateString,
  htmlStringSanitizer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacBadge,
  PharmacButton,
  PharmacCarousel,
  PharmacCarouselItem,
  PharmacH4,
  PharmacIcon,
  PharmacImage,
  PharmacPortalTooltip,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { useShoppingCartContext } from '../navigation/shopping-cart/ShoppingCartContext'
import { NoResultsResponse } from '../responses/NoResultsResponse'
import { useShop } from './useShop'
import { formatPriceDecimal } from './util/shop-utils'

const InfoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: var(--spacing-small);

  p {
    ${MediaQuery.maxTablet} {
      margin-bottom: var(--spacing-tiny);
    }
  }
`

const Image = styled(PharmacImage)`
  max-width: 100% !important;
  max-height: 440px !important;
`

const Tag = styled(PharmacBadge)`
  ${MediaQuery.maxTablet} {
    margin-bottom: var(--spacing-tiny);
  }
`

export const ShopProductDocument = ({
  documentProduct,
}: {
  documentProduct: ExtranetShopProductDto
}) => {
  const { t, i18n } = useLanguage()
  const { minDesktop, maxMobile, minTablet } = useBreakpoint()
  const { customer } = useCustomerContext()

  const {
    hasBuyerRole,
    subscribeToFreeProduct,
    accessPeriodDuration,
    productCategory,
    myRoles,
    hasBuyingBan,
    hasRequiredRoles,
  } = useShop(documentProduct.productId, documentProduct)
  const { isPharmacCustomerService, user } = useAuthenticationContext()

  const hook = useShoppingCartContext()

  const getDocument = async () => {
    const response = await api.shop.getProductDocument(
      customer!.id,
      Number(documentProduct?.productId),
      {
        format: 'blob',
      }
    )

    const blob = new Blob([response as unknown as string], {
      type: 'application/pdf',
    })

    window.open(window.URL.createObjectURL(blob), '_blank')
  }

  if (!documentProduct) return null

  return (
    <ExtranetContainer
      flexColumn
      paddingSmallHorizontal
      paddingLargeBottom={!minDesktop}
      full
    >
      <ExtranetContainer
        full
        paddingSmallVertical={minDesktop}
        paddingMediumTop={!minDesktop}
      >
        <InfoContainer>
          {documentProduct.language && (
            <PharmacText
              marginLargeRight={minDesktop}
              marginMediumRight={!minDesktop}
            >
              {t(`common:${documentProduct.language?.code as 'fi' | 'sv' | 'en'}`)}
            </PharmacText>
          )}
          <PharmacText
            marginLargeRight={minDesktop}
            marginMediumRight={!minDesktop}
          >
            {productCategory(documentProduct.productFamily)}
          </PharmacText>
          <PharmacText
            marginLargeRight={minDesktop}
            marginMediumRight={!minDesktop}
          >
            {t('extranet:shop.published')}:{' '}
            {documentProduct?.publishDate && formatDateString(documentProduct?.publishDate)}
          </PharmacText>
          <PharmacText marginLargeRight>{documentProduct?.manufacturer?.name}</PharmacText>

          {minDesktop &&
            documentProduct?.tags?.map((a: any) => (
              <Tag
                key={a.id}
                variant="primary"
                marginSmallRight
                small
              >
                {a.name}
              </Tag>
            ))}
        </InfoContainer>
      </ExtranetContainer>
      {!minDesktop && (
        <ExtranetContainer
          full
          paddingSmallVertical
          flexWrap
        >
          {documentProduct?.tags?.map((a: any) => (
            <Tag
              key={a.id}
              variant="primary"
              marginSmallRight
              small
            >
              {a.name}
            </Tag>
          ))}
        </ExtranetContainer>
      )}
      <ExtranetContainer
        marginSmallTop
        full
        flexColumn
        paddingMediumBottom={!minDesktop}
      >
        <PharmacH4 marginMediumBottom>{documentProduct?.title}</PharmacH4>
      </ExtranetContainer>
      <ExtranetContainer
        flexColumn
        full
        paddingSmall
        style={{
          backgroundColor: 'var(--color-accent-2)',
          borderRadius: '15px',
          border: '1.5px solid var(--color-accent-4)',
        }}
      >
        {documentProduct?.images?.length !== 0 ? (
          <PharmacCarousel>
            {(documentProduct?.images || []).map((a) => (
              <PharmacCarouselItem key={a.id}>
                <Image imageUrl={maxMobile ? a.mobileUrl : a.desktopWideUrl} />
              </PharmacCarouselItem>
            ))}
          </PharmacCarousel>
        ) : (
          <NoResultsResponse
            icon="image"
            title={t('extranet:shop.product.pictureNotAvailable')}
            description=" "
          />
        )}
        <ExtranetContainer
          flexRow={minTablet}
          flexColumn={!minTablet}
          full
        >
          <ExtranetContainer
            third={minTablet}
            full={!minTablet}
            paddingLargeVertical
            paddingMediumHorizontal
            flexColumn
            alignCenter={maxMobile}
          >
            {documentProduct.productFamily === 'document-product' && (
              <>
                <ExtranetContainer
                  flexRow
                  alignCenter
                  paddingLargeBottom
                >
                  {!documentProduct.isSubscribed && (
                    <>
                      <PharmacIcon
                        icon="alertCircle"
                        style={{ color: 'var(--color-warning)' }}
                      />
                      <PharmacText paddingSmallLeft>
                        {t('extranet:shop.product.notActivated')}
                      </PharmacText>
                    </>
                  )}
                  {documentProduct.isSubscribed && (
                    <>
                      <PharmacIcon
                        icon="check"
                        style={{ color: 'var(--color-success)' }}
                      />
                      <PharmacText paddingSmallLeft>
                        {t('extranet:shop.product.activated')}
                      </PharmacText>
                    </>
                  )}
                </ExtranetContainer>
                <ExtranetContainer
                  flexRow
                  alignCenter
                  paddingLargeBottom
                >
                  <>
                    <PharmacIcon
                      icon="flag"
                      style={{ color: 'var(--color-primary)' }}
                    />
                    {(!documentProduct.isSubscribed && documentProduct.accessPeriod?.isUnlimited) ||
                    (documentProduct.isSubscribed && !documentProduct.subscriptionEndDate) ? (
                      <PharmacText paddingSmallLeft>
                        {t('extranet:shop.product.unlimitedAccess')}
                      </PharmacText>
                    ) : (
                      <>
                        <PharmacText paddingSmallLeft>
                          {t('extranet:shop.product.viewable')}
                          {documentProduct.isSubscribed
                            ? ` ${i18n.language !== 'fi' ? t('extranet:shop.product.till') : ''} ${documentProduct.subscriptionEndDate ? formatDateString(documentProduct.subscriptionEndDate) : '-'} ${i18n.language === 'fi' ? t('extranet:shop.product.till') : ''}`
                            : ':'}
                        </PharmacText>
                        {!documentProduct.isSubscribed && (
                          <>
                            <PharmacText paddingSmallLeft>
                              {documentProduct.accessPeriod?.duration}
                            </PharmacText>
                            {accessPeriodDuration(documentProduct.accessPeriod?.durationUnit)}
                          </>
                        )}
                      </>
                    )}
                  </>
                </ExtranetContainer>
              </>
            )}
            <ExtranetContainer
              flexRow
              justifyCenter
              alignCenter
              paddingLargeBottom
            >
              <ExtranetContainer
                alignCenter
                justifyCenter
                style={{
                  backgroundColor: '#B87EBC80',
                  height: '100%',
                  width: 'fit-content',
                  borderRadius: '25px',
                }}
              >
                <PharmacIcon
                  style={{ margin: 'var(--spacing-tiny)' }}
                  size="15px"
                  icon="euro"
                />
              </ExtranetContainer>
              {documentProduct.unitSellingPrice !== 0 ? (
                <>
                  <PharmacText
                    paddingSmallHorizontal
                    weight="bold"
                  >
                    {formatPriceDecimal(documentProduct.unitSellingPrice, 2)}
                  </PharmacText>
                  <PharmacText>
                    + {t('extranet:physicalProducts.vatLowercase')}{' '}
                    {formatPriceDecimal(Number(documentProduct.taxRate?.percentage))}%
                  </PharmacText>
                </>
              ) : (
                <PharmacText paddingSmallHorizontal>{t('common:free')}</PharmacText>
              )}
            </ExtranetContainer>

            <ExtranetContainer
              paddingLargeBottom
              flexColumn
              alignCenter
              full={minTablet}
            >
              {!documentProduct.isSubscribed && (
                <ExtranetContainer
                  flexRow
                  alignCenter
                  full
                >
                  {documentProduct.unitSellingPrice !== 0 ||
                  documentProduct.productFamily === 'service-product' ||
                  documentProduct.productFamily === 'physical-product' ? (
                    <>
                      <PharmacButton
                        fullWidth={minDesktop}
                        onClick={() =>
                          hook.addAndRemoveCartButton(documentProduct, documentProduct.productId)
                        }
                        disabled={
                          (!myRoles?.isAdmin &&
                            !hasBuyerRole &&
                            documentProduct.unitSellingPrice !== 0) ||
                          (hasBuyingBan && documentProduct.unitSellingPrice !== 0) ||
                          isPharmacCustomerService
                        }
                        variant={
                          (!myRoles?.isAdmin &&
                            !hasBuyerRole &&
                            documentProduct.unitSellingPrice !== 0) ||
                          (hasBuyingBan && documentProduct.unitSellingPrice !== 0) ||
                          isPharmacCustomerService
                            ? 'secondary'
                            : 'primary'
                        }
                      >
                        <PharmacIcon
                          size="20px"
                          icon="shoppingCart"
                        />
                        <PharmacText paddingSmallLeft>
                          {hook.selectedProducts[documentProduct.productId]?.includes(
                            documentProduct.productId
                          ) ? (
                            <>{t('extranet:shop.buttons.removeFromCart')}</>
                          ) : (
                            <>{t('extranet:shop.buttons.addToCart')}</>
                          )}
                        </PharmacText>
                      </PharmacButton>
                      {!myRoles?.isAdmin &&
                        !hasBuyerRole &&
                        !hasBuyingBan &&
                        documentProduct.unitSellingPrice !== 0 && (
                          <PharmacPortalTooltip
                            placement="top"
                            paddingSmallLeft
                            backgroundColor="var(--color-white)"
                            handle={
                              <PharmacIcon
                                style={{ color: 'var(--color-info)' }}
                                size="20px"
                                icon="info"
                              />
                            }
                          >
                            <PharmacText>{t('extranet:shop.noBuyerRoleTooltip')}</PharmacText>
                          </PharmacPortalTooltip>
                        )}
                      {hasBuyingBan && documentProduct.unitSellingPrice !== 0 && (
                        <PharmacTooltip
                          paddingSmallLeft
                          backgroundColor="var(--color-white)"
                          handle={
                            <PharmacIcon
                              style={{ color: 'var(--color-info)' }}
                              size="20px"
                              icon="info"
                            />
                          }
                        >
                          <PharmacText>{t('extranet:shop.purchaseRightDeniedTooltip')}</PharmacText>
                        </PharmacTooltip>
                      )}
                    </>
                  ) : (
                    <>
                      <PharmacButton
                        fullWidth={minDesktop}
                        onClick={() => subscribeToFreeProduct()}
                        disabled={!documentProduct.canBeFreeSubscribed || isPharmacCustomerService}
                        variant={
                          !documentProduct.canBeFreeSubscribed || isPharmacCustomerService
                            ? 'secondary'
                            : 'primary'
                        }
                      >
                        <PharmacIcon
                          size="20px"
                          icon="plus"
                        />
                        <PharmacText>{t('extranet:shop.buttons.addToOwnProducts')}</PharmacText>
                      </PharmacButton>
                      {!documentProduct.canBeFreeSubscribed && (
                        <PharmacPortalTooltip
                          placement="top"
                          paddingSmallLeft
                          backgroundColor="var(--color-white)"
                          handle={
                            <PharmacIcon
                              style={{ color: 'var(--color-info)' }}
                              size="20px"
                              icon="info"
                            />
                          }
                        >
                          <PharmacText>{t('extranet:shop.noRequiredProfRolesTooltip')}</PharmacText>
                        </PharmacPortalTooltip>
                      )}
                    </>
                  )}
                </ExtranetContainer>
              )}
              {documentProduct.isSubscribed && (
                <>
                  {documentProduct.productFamily === 'document-product' && (
                    <ExtranetContainer
                      flexRow
                      alignCenter
                      full
                      marginSmallBottom
                    >
                      <PharmacButton
                        fullWidth={minDesktop}
                        onClick={() => getDocument()}
                        disabled={
                          !hasRequiredRoles(
                            documentProduct.professionalRoles,
                            user?.professionalRoles
                          )
                        }
                        variant={
                          !hasRequiredRoles(
                            documentProduct.professionalRoles,
                            user?.professionalRoles
                          )
                            ? 'secondary'
                            : 'primary'
                        }
                      >
                        <PharmacIcon
                          size="20px"
                          icon="download"
                        />
                        <PharmacText paddingSmallLeft>
                          {t('extranet:shopProducts.downloadDocument')}
                        </PharmacText>
                      </PharmacButton>
                      {!hasRequiredRoles(
                        documentProduct.professionalRoles,
                        user?.professionalRoles
                      ) && (
                        <PharmacPortalTooltip
                          placement="top"
                          paddingSmallLeft
                          backgroundColor="var(--color-white)"
                          handle={
                            <PharmacIcon
                              style={{ color: 'var(--color-info)' }}
                              size="20px"
                              icon="info"
                            />
                          }
                        >
                          <PharmacText>
                            {t('extranet:shop.noRequiredProfRolesForDownloadTooltip')}
                          </PharmacText>
                        </PharmacPortalTooltip>
                      )}
                    </ExtranetContainer>
                  )}
                  {documentProduct.unitSellingPrice !== 0 && (
                    <ExtranetContainer
                      full
                      alignCenter
                    >
                      <PharmacButton
                        fullWidth={minDesktop}
                        onClick={() =>
                          hook.addAndRemoveCartButton(documentProduct, documentProduct.productId)
                        }
                        variant={
                          (!myRoles?.isAdmin &&
                            !hasBuyerRole &&
                            documentProduct.unitSellingPrice !== 0) ||
                          (hasBuyingBan && documentProduct.unitSellingPrice !== 0) ||
                          isPharmacCustomerService
                            ? 'secondary'
                            : 'primary'
                        }
                        disabled={
                          (!myRoles?.isAdmin &&
                            !hasBuyerRole &&
                            documentProduct.unitSellingPrice !== 0) ||
                          (hasBuyingBan && documentProduct.unitSellingPrice !== 0) ||
                          isPharmacCustomerService
                        }
                      >
                        <PharmacIcon
                          size="15px"
                          icon="shoppingCart"
                        />
                        <PharmacText paddingSmallHorizontal>
                          <PharmacText paddingSmallHorizontal>
                            {hook.selectedProducts &&
                            hook.selectedProducts[documentProduct.productId]?.includes(
                              documentProduct.productId
                            ) ? (
                              <>{t('extranet:shop.buttons.removeFromCart')}</>
                            ) : (
                              <> {t('extranet:shop.buttons.buyAgain')}</>
                            )}
                          </PharmacText>
                        </PharmacText>
                      </PharmacButton>
                      {hasBuyingBan && documentProduct.unitSellingPrice !== 0 && (
                        <PharmacTooltip
                          paddingSmallLeft
                          backgroundColor="var(--color-white)"
                          handle={
                            <PharmacIcon
                              style={{ color: 'var(--color-info)' }}
                              size="20px"
                              icon="info"
                            />
                          }
                        >
                          <PharmacText>{t('extranet:shop.purchaseRightDeniedTooltip')}</PharmacText>
                        </PharmacTooltip>
                      )}
                      {!myRoles?.isAdmin &&
                        !hasBuyerRole &&
                        !hasBuyingBan &&
                        documentProduct.unitSellingPrice !== 0 && (
                          <PharmacPortalTooltip
                            placement="top"
                            paddingSmallLeft
                            backgroundColor="var(--color-white)"
                            handle={
                              <PharmacIcon
                                style={{ color: 'var(--color-info)' }}
                                size="20px"
                                icon="info"
                              />
                            }
                          >
                            <PharmacText>{t('extranet:shop.noBuyerRoleTooltip')}</PharmacText>
                          </PharmacPortalTooltip>
                        )}
                    </ExtranetContainer>
                  )}
                </>
              )}
            </ExtranetContainer>

            {documentProduct.productFamily === 'document-product' && (
              <ExtranetContainer
                full
                style={{ border: '2px solid var(--color-accent-4)', borderRadius: '8px' }}
              >
                <ExtranetContainer
                  paddingSmall
                  flexColumn
                >
                  <PharmacText
                    weight="bold"
                    color="purple"
                    paddingMediumBottom
                  >
                    {t('extranet:shop.product.requiredRoles')}
                  </PharmacText>

                  {documentProduct?.professionalRoles.length !== 0 ? (
                    documentProduct?.professionalRoles.map((role) => (
                      <PharmacText
                        key={role.id}
                        paddingSmallBottom
                      >
                        {role.displayName}
                      </PharmacText>
                    ))
                  ) : (
                    <PharmacText paddingSmallBottom>
                      {t('extranet:shop.product.noRequiredRoles')}
                    </PharmacText>
                  )}
                </ExtranetContainer>
              </ExtranetContainer>
            )}
          </ExtranetContainer>
          <div
            style={
              minTablet
                ? {
                    borderLeft: '2px solid var(--color-accent-4)',
                    height: '-webkit-fill-available',
                    marginTop: 'var(--spacing-large)',
                    marginBottom: 'var(--spacing-large)',
                  }
                : undefined
            }
          />
          <ExtranetContainer
            flexColumn
            full
            marginLargeTop={minTablet}
            marginLargeBottom
          >
            <ExtranetContainer
              flexColumn
              paddingLargeHorizontal={minTablet}
              paddingMedium={!minTablet}
              full
            >
              <ExtranetTextDiv
                data={documentProduct?.content}
                dangerouslySetInnerHTML={{
                  __html: htmlStringSanitizer(documentProduct?.content),
                }}
              />
            </ExtranetContainer>
          </ExtranetContainer>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
